import { copyToClipboard } from '@/utils/dom'
import type { GameState } from '@/types'

export class ReferralManager {
  private refInput: HTMLInputElement
  private copyBtn: HTMLButtonElement
  private refEarnedSpan: HTMLSpanElement
  private shareWhatsAppBtn: HTMLButtonElement
  private shareTelegramBtn: HTMLButtonElement
  private shareMoreBtn: HTMLButtonElement
  private stickerPackBtn: HTMLButtonElement

  constructor() {
    this.refInput = document.getElementById('refLink') as HTMLInputElement
    this.copyBtn = document.getElementById('copyRef') as HTMLButtonElement
    this.refEarnedSpan = document.getElementById('refEarned') as HTMLSpanElement
    this.shareWhatsAppBtn = document.getElementById('shareWhatsApp') as HTMLButtonElement
    this.shareTelegramBtn = document.getElementById('shareTelegram') as HTMLButtonElement
    this.shareMoreBtn = document.getElementById('shareMore') as HTMLButtonElement
    this.stickerPackBtn = document.getElementById('getStickerPack') as HTMLButtonElement
    
    this.setupCopyButton()
    this.setupShareButtons()
    this.setupStickerPackButton()
  }

  private setupCopyButton(): void {
    this.copyBtn.addEventListener('click', async () => {
      try {
        await copyToClipboard(this.refInput.value)
        import('@/services/analytics').then(({ track }) => track('referral_link_copied', { destination: 'clipboard' }))
        this.showCopyFeedback()
      } catch (error) {
        console.error('Failed to copy:', error)
      }
    })
  }

  private showCopyFeedback(): void {
    const originalText = this.copyBtn.textContent!
    this.copyBtn.textContent = '✓ Copied!'
    this.copyBtn.classList.add('copied')
    this.copyBtn.disabled = true

    setTimeout(() => {
      this.copyBtn.textContent = originalText
      this.copyBtn.disabled = false
      this.copyBtn.classList.remove('copied')
    }, 1500)
  }

  private setupShareButtons(): void {
    this.shareWhatsAppBtn.addEventListener('click', () => {
      this.shareToWhatsApp()
    })

    this.shareTelegramBtn.addEventListener('click', () => {
      this.shareToTelegram()
    })

    this.shareMoreBtn.addEventListener('click', () => {
      this.shareGeneric()
    })
  }

  private shareToWhatsApp(): void {
    const message = `🍪 Join me in cracking fortune cookies and earning rewards! ${this.refInput.value}`
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`
    import('@/services/analytics').then(({ track }) => track('referral_shared', { channel: 'whatsapp' }))
    window.open(url, '_blank')
  }

  private shareToTelegram(): void {
    const message = `🍪 Join me in cracking fortune cookies and earning rewards!`
    const url = `https://t.me/share/url?url=${encodeURIComponent(this.refInput.value)}&text=${encodeURIComponent(message)}`
    import('@/services/analytics').then(({ track }) => track('referral_shared', { channel: 'telegram' }))
    window.open(url, '_blank')
  }

  private shareGeneric(): void {
    if (typeof navigator.share === 'function') {
      import('@/services/analytics').then(({ track }) => track('referral_shared', { channel: 'native' }))
      navigator.share({
        title: 'Fortune Cookie Game',
        text: '🍪 Join me in cracking fortune cookies and earning rewards!',
        url: this.refInput.value
      }).catch(console.error)
    } else {
      // Fallback to copying
      copyToClipboard(this.refInput.value)
      import('@/services/analytics').then(({ track }) => track('referral_link_copied', { destination: 'clipboard' }))
      this.showCopyFeedback()
    }
  }

  updateFromGameState(state: GameState): void {
    // Update referral link
    if (!state.refCode) {
      this.refInput.value = 'Generating link…'
    } else {
      this.refInput.value = `https://t.me/a_fortune_cookie_bot/cookie?startapp=${state.refCode}`
    }

    // Update referral earnings
    this.refEarnedSpan.textContent = String(state.refEarned || 0)
  }

  private setupStickerPackButton(): void {
    this.stickerPackBtn.addEventListener('click', () => {
      // Track analytics
      import('@/services/analytics').then(({ track }) => {
        track('sticker_pack_clicked')
      })

      // Open sticker pack in Telegram
      const stickerPackUrl = 'https://t.me/addstickers/AFortuneCookieBot'
      
      // Try to use Telegram WebApp if available
      if (window.Telegram?.WebApp?.openTelegramLink) {
        window.Telegram.WebApp.openTelegramLink(stickerPackUrl)
      } else {
        // Fallback to regular window.open
        window.open(stickerPackUrl, '_blank')
      }
    })
  }
}
